<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto" class="d-flex align-center" style="gap: 12px;">
        <v-radio-group
          v-model="filterType"
          inline
          density="compact"
          hide-details
          class="flex-grow-0"
        >
          <v-radio
            v-for="t in typeOptions"
            :key="t.value"
            :label="t.text"
            :value="t.value"
          ></v-radio>
        </v-radio-group>
        <v-select
          v-model="filterStatus"
          :items="statusFilterOptions"
          item-title="text"
          item-value="value"
          placeholder="Все статусы"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="min-width: 160px"
        ></v-select>
        <v-select
          v-model="filterKind"
          :items="kindOptions"
          item-title="text"
          item-value="value"
          placeholder="Все виды"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="min-width: 160px"
        ></v-select>
        <v-select
          v-model="filterProvider"
          :items="providerFilterOptions"
          item-title="text"
          item-value="value"
          placeholder="Все провайдеры"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="min-width: 180px"
        ></v-select>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">Управление прокси</h4>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          variant="elevated"
          @click="openAddDialog"
          prepend-icon="mdi-plus"
        >
          Добавить прокси
        </v-btn>
      </v-col>
    </v-row>

    <v-card>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="filteredProxies"
          :items-per-page="-1"
          :loading="isLoading"
          class="elevation-1"
          hover
          fixed-header
          height="calc(100vh - 236px)"
          hide-default-footer
        >
          <template v-slot:[`item.ip_port`]="{ item }">
            <span class="proxy-addr">{{ item.ip_port }}</span>
          </template>

          <template v-slot:[`item.proxy_type`]="{ item }">
            <v-chip size="small" variant="tonal">{{ (item.proxy_type || '').toUpperCase() }}</v-chip>
          </template>

          <template v-slot:[`item.kind`]="{ item }">
            {{ kindLabel(item.kind) }}
          </template>

          <!-- provider: click to set or change it in place -->
          <template v-slot:[`item.provider`]="{ item }">
            <v-menu
              v-model="providerMenu[item.id]"
              :close-on-content-click="false"
              location="bottom start"
              @update:model-value="(open) => open && (providerDraft[item.id] = item.provider)"
            >
              <template v-slot:activator="{ props }">
                <span v-bind="props" class="provider-cell" title="Изменить провайдера">
                  <template v-if="item.provider">{{ item.provider }}</template>
                  <span v-else class="text-medium-emphasis">—</span>
                </span>
              </template>
              <v-card min-width="260">
                <v-card-text class="pa-3">
                  <v-combobox
                    v-model="providerDraft[item.id]"
                    :items="knownProviders"
                    label="Провайдер"
                    variant="outlined"
                    density="compact"
                    clearable
                    autofocus
                    hide-details
                    maxlength="100"
                    @keydown.enter="saveProvider(item)"
                  ></v-combobox>
                </v-card-text>
                <v-card-actions class="pt-0">
                  <v-spacer />
                  <v-btn variant="text" @click="providerMenu[item.id] = false">Отмена</v-btn>
                  <v-btn color="primary" variant="text" :loading="providerSaving === item.id"
                         @click="saveProvider(item)">Сохранить</v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </template>

          <template v-slot:[`item.country_code`]="{ item }">
            <v-chip v-if="item.country_code" size="x-small" variant="tonal">{{ item.country_code.toUpperCase() }}</v-chip>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="item.status === 'OK' ? 'success' : 'error'" size="small" variant="tonal">
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:[`item.account_logins`]="{ item }">
            <span v-if="item.account_logins.length">{{ item.account_logins.join(', ') }}</span>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <!-- how real requests through the proxy ended in the last hour, per platform; red =
               held back from that platform for a while (proxy/app/services/proxy_health.py) -->
          <template v-slot:[`item.health`]="{ item }">
            <template v-if="item.health && Object.keys(item.health).length">
              <v-chip
                v-for="[task, h] in sortedHealth(item.health)"
                :key="task"
                size="x-small"
                variant="tonal"
                class="mr-1 mb-1"
                :color="healthColor(h)"
                :title="healthTitle(task, h)"
              >{{ platformName(task) }} {{ h.success_rate === null ? '—' : Math.round(h.success_rate * 100) + '%' }}</v-chip>
            </template>
            <span v-else class="text-medium-emphasis">—</span>
          </template>

          <template v-slot:[`item.last_used`]="{ item }">
            {{ formatDateTime(item.last_used) }}
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip text="Проверить сейчас" location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-refresh"
                  size="small"
                  variant="text"
                  color="success"
                  :loading="checkingId === item.id"
                  @click="checkProxy(item)"
                ></v-btn>
              </template>
            </v-tooltip>
            <v-tooltip :text="item.account_logins.length ? 'Закреплён за учётной записью' : 'Удалить'" location="top">
              <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <v-btn
                    icon="mdi-delete"
                    size="small"
                    variant="text"
                    color="error"
                    :disabled="item.account_logins.length > 0"
                    @click="confirmDelete(item)"
                  ></v-btn>
                </span>
              </template>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add Dialog -->
    <v-dialog v-model="showDialog" max-width="600" @keydown.esc="showDialog = false">
      <v-card>
        <v-card-title>Добавить прокси</v-card-title>
        <v-card-text>
          <v-alert v-if="dialogError" type="error" variant="tonal" density="compact" class="mb-3" closable @click:close="dialogError = ''">
            {{ dialogError }}
          </v-alert>
          <template v-if="!result">
            <v-textarea
              v-model="form.text"
              label="Прокси"
              variant="outlined"
              density="compact"
              rows="8"
              placeholder="ip:port:login:pass&#10;ip:port&#10;login:pass@ip:port"
              hint="По одному в строке; можно через запятую, точку с запятой или пробел"
              persistent-hint
              class="mb-3 proxy-addr"
            ></v-textarea>
            <v-select
              v-model="form.proxy_type"
              :items="typeOptions"
              item-title="text"
              item-value="value"
              label="Тип"
              variant="outlined"
              density="compact"
            ></v-select>
            <v-select
              v-model="form.kind"
              :items="addKindOptions"
              item-title="text"
              item-value="value"
              label="Вид"
              variant="outlined"
              density="compact"
            ></v-select>
            <v-text-field
              v-model="form.country_code"
              label="Страна (код, напр. US)"
              variant="outlined"
              density="compact"
              maxlength="2"
            ></v-text-field>
            <!-- one provider for the whole batch: pick a known one or type a new name -->
            <v-combobox
              v-model="form.provider"
              :items="knownProviders"
              label="Провайдер"
              variant="outlined"
              density="compact"
              clearable
              maxlength="100"
            ></v-combobox>
          </template>
          <template v-else>
            <div class="mb-2">
              Добавлено: <strong>{{ result.created.length }}</strong>,
              уже были: <strong>{{ result.skipped.length }}</strong>,
              ошибок: <strong>{{ result.errors.length }}</strong>
            </div>
            <v-list v-if="result.skipped.length || result.errors.length" density="compact">
              <v-list-item v-for="s in result.skipped" :key="`s-${s.ip_port}-${s.proxy_type}`" class="proxy-addr">
                <v-icon size="small" color="info" class="mr-2">mdi-content-duplicate</v-icon>
                {{ s.ip_port }} ({{ s.proxy_type.toUpperCase() }}) — уже есть
              </v-list-item>
              <v-list-item v-for="e in result.errors" :key="`e-${e.position}`" class="proxy-addr">
                <v-icon size="small" color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
                № {{ e.position }} ({{ e.host }}) — {{ e.error }}
              </v-list-item>
            </v-list>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <template v-if="!result">
            <v-btn variant="text" @click="showDialog = false">Отмена</v-btn>
            <v-btn color="primary" variant="elevated" :loading="isSaving" @click="addProxies">Добавить</v-btn>
          </template>
          <v-btn v-else color="primary" variant="elevated" @click="showDialog = false">Готово</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Удаление прокси</v-card-title>
        <v-card-text>
          Удалить прокси <strong>{{ deleteTarget?.ip_port }}</strong>
          ({{ (deleteTarget?.proxy_type || '').toUpperCase() }})?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Отмена</v-btn>
          <v-btn color="error" variant="elevated" @click="doDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient';

const NO_PROVIDER = '__none__';
const EMPTY_FORM = { text: '', proxy_type: 'http', kind: 'datacenter', country_code: '', provider: null };

export default {
  name: 'ProxiesManagement',
  data() {
    return {
      proxies: [],
      isLoading: false,
      filterType: 'http',
      filterStatus: null,
      filterKind: null,
      filterProvider: null,
      providerMenu: {},
      providerDraft: {},
      providerSaving: null,
      headers: [
        { title: 'ID', key: 'id', width: '70px' },
        { title: 'Адрес', key: 'ip_port' },
        { title: 'Тип', key: 'proxy_type', width: '90px' },
        { title: 'Вид', key: 'kind', width: '130px' },
        { title: 'Страна', key: 'country_code', width: '90px' },
        { title: 'Провайдер', key: 'provider', width: '150px' },
        { title: 'Статус', key: 'status', width: '110px' },
        { title: 'Платформы за час', key: 'health', sortable: false, width: '260px' },
        { title: 'Учётные записи', key: 'account_logins', sortable: false },
        { title: 'Последнее использование', key: 'last_used', width: '180px' },
        { title: 'Добавлен', key: 'created_at', width: '150px' },
        { title: 'Действия', key: 'actions', sortable: false, width: '120px' },
      ],
      typeOptions: [
        { text: 'HTTP', value: 'http' },
        { text: 'SOCKS', value: 'socks' },
      ],
      kindOptions: [
        { text: 'Датацентр', value: 'datacenter' },
        { text: 'Резидентный', value: 'residential' },
        { text: 'Tor', value: 'tor' },
      ],
      statusFilterOptions: [
        { text: 'OK', value: 'OK' },
        { text: 'FAILED', value: 'FAILED' },
      ],
      // Add dialog
      showDialog: false,
      isSaving: false,
      dialogError: '',
      form: { ...EMPTY_FORM },
      result: null,
      // Check
      checkingId: null,
      // Delete
      showDeleteDialog: false,
      deleteTarget: null,
      // Snackbar
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    addKindOptions() {
      return this.kindOptions.filter((k) => k.value !== 'tor');
    },
    filteredProxies() {
      return this.proxies.filter((p) =>
        p.proxy_type === this.filterType &&
        (!this.filterStatus || p.status === this.filterStatus) &&
        (!this.filterKind || p.kind === this.filterKind) &&
        (!this.filterProvider || (this.filterProvider === NO_PROVIDER ? !p.provider : p.provider === this.filterProvider)));
    },
    // providers already in use: the choices of the add form and of the row editor
    knownProviders() {
      return [...new Set(this.proxies.map((p) => p.provider).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    providerFilterOptions() {
      return [...this.knownProviders.map((p) => ({ text: p, value: p })), { text: 'Без провайдера', value: NO_PROVIDER }];
    },
  },
  async mounted() {
    await this.loadProxies();
  },
  methods: {
    async saveProvider(item) {
      this.providerSaving = item.id;
      try {
        const provider = (this.providerDraft[item.id] || '').trim() || null;
        const resp = await apiClient.patch(`/admin/proxies/${item.id}`, { provider });
        const index = this.proxies.findIndex((p) => p.id === item.id);
        if (index >= 0) this.proxies.splice(index, 1, { ...this.proxies[index], provider: resp.data.provider });
        this.providerMenu[item.id] = false;
        this.showSnackbar('Провайдер сохранён');
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Не удалось сохранить провайдера', 'error');
      } finally {
        this.providerSaving = null;
      }
    },
    platformName(task) {
      return {
        telegram_web: 'Telegram', x_web: 'X', dzen_web: 'Дзен', user_web: 'Веб',
        search_serp: 'Выдача', search_crawl: 'Поиск', edit_recrawl: 'Перепроверка', onion_search: 'Tor',
      }[task] || task;
    },
    // the same order in every row, so a platform is always in its place; unknown ones last
    sortedHealth(health) {
      const order = ['telegram_web', 'x_web', 'dzen_web', 'user_web', 'search_crawl', 'search_serp',
        'edit_recrawl', 'onion_search'];
      const rank = (task) => (order.includes(task) ? order.indexOf(task) : order.length);
      return Object.entries(health).sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
    },
    healthColor(h) {
      if (h.cooldown) return 'error';
      if (h.success_rate === null) return undefined;
      if (h.success_rate >= 0.7) return 'success';
      return h.success_rate >= 0.3 ? 'warning' : 'error';
    },
    healthTitle(task, h) {
      const reasons = {
        timeout: 'таймауты', blocked: 'отказы сайта', connection: 'ошибки соединения', other: 'прочие ошибки',
      };
      const parts = [`${this.platformName(task)}: запросов за час ${h.total}`];
      if (h.top_failure) parts.push(`чаще всего — ${reasons[h.top_failure] || h.top_failure}`);
      if (h.cooldown) parts.push('сейчас не выдаётся этой платформе');
      return parts.join('; ');
    },
    async loadProxies() {
      this.isLoading = true;
      try {
        const resp = await apiClient.get('/admin/proxies');
        this.proxies = resp.data.items || [];
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Не удалось загрузить прокси', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    kindLabel(kind) {
      return { datacenter: 'Датацентр', residential: 'Резидентный', tor: 'Tor' }[kind] || kind;
    },
    openAddDialog() {
      this.form = { ...EMPTY_FORM, proxy_type: this.filterType };
      this.result = null;
      this.dialogError = '';
      this.showDialog = true;
    },
    async addProxies() {
      if (!this.form.text.trim()) {
        this.dialogError = 'Вставьте хотя бы один прокси';
        return;
      }
      this.isSaving = true;
      try {
        const payload = {
          ...this.form,
          country_code: this.form.country_code.trim() || null,
          provider: (this.form.provider || '').trim() || null,
        };
        const resp = await apiClient.post('/admin/proxies', payload);
        this.result = resp.data;
        this.form.text = '';
        if (this.result.created.length) await this.loadProxies();
      } catch (e) {
        const detail = e.response?.data?.detail;
        this.dialogError = Array.isArray(detail) ? 'Проверьте код страны' : (detail || 'Ошибка сохранения');
      } finally {
        this.isSaving = false;
      }
    },
    async checkProxy(proxy) {
      this.checkingId = proxy.id;
      try {
        const resp = await apiClient.post(`/admin/proxies/${proxy.id}/check`, null, { timeout: 100000 });
        Object.assign(proxy, resp.data);
        this.showSnackbar(`${proxy.ip_port}: ${resp.data.status}`, resp.data.status === 'OK' ? 'success' : 'error');
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Ошибка проверки', 'error');
      } finally {
        this.checkingId = null;
      }
    },
    confirmDelete(proxy) {
      this.deleteTarget = proxy;
      this.showDeleteDialog = true;
    },
    async doDelete() {
      try {
        await apiClient.delete(`/admin/proxies/${this.deleteTarget.id}`);
        this.showDeleteDialog = false;
        this.showSnackbar('Прокси удалён');
        await this.loadProxies();
      } catch (e) {
        this.showDeleteDialog = false;
        this.showSnackbar(e.response?.data?.detail || 'Ошибка удаления', 'error');
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '—';
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
          ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      } catch { return dateStr; }
    },
    showSnackbar(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
.provider-cell {
  cursor: pointer;
  border-bottom: 1px dashed rgba(var(--v-theme-on-surface), 0.3);
}
.proxy-addr {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.8rem;
}
</style>
